import type { ReactNode } from 'react';
import { IconCircleCheck, IconClock } from '@tabler/icons-react';
import { filterBl, finalLeft, releaseStepLabel } from './logic';
import { NOW_MIN, RELEASE_STEPS } from './fixtures';
import type { BillType, BlAmend, BlFinal, BlRelease, BlTab, LoiTemplate, Manifest, OriginalDoc } from './types';
import s from './bl.module.css';

// Bảng các tab ngoài Draft của Mint-BL (code board `tab === 'final' | 'rel' | 'amd' | 'loi' | 'orig' | 'mnf'`). Chỉ đọc. Tiền ẩn (money = hide): chỉ hiện Có / Không.

type Tone = 'grey' | 'violet' | 'green' | 'amber' | 'red' | 'blue';
type Mood = 'err' | 'ok' | 'warn' | 'lb' | 'tx2';
interface Cell { kind: 'link' | 'txt' | 'chip'; v: string; sub?: string; tone?: Tone; mood?: Mood; strong?: boolean; clock?: boolean; tip?: string }
interface Act { k: string; cls: 'pri' | 'ghost' | 'done' }
interface Row { id: string; cells: Cell[]; act: Act }
export interface TabTable { cols: readonly (readonly [string, number])[]; rows: Row[]; foot: string; empty: string; emptySub: string }

const TYPE_TONE: Record<BillType, Tone> = { Original: 'grey', Surrender: 'violet', Seaway: 'green', Telex: 'grey' };
const lk = (v: string, sub?: string): Cell => ({ kind: 'link', v, sub });
const tx = (v: string, sub?: string, o: Partial<Cell> = {}): Cell => ({ kind: 'txt', v, sub, ...o });
const chip = (v: string, tone: Tone, tip?: string): Cell => ({ kind: 'chip', v, tone, tip });
const yn = (x: number) => (x > 0 ? 'Có' : 'Không');

export interface TabData {
  finals: readonly BlFinal[]; releases: readonly BlRelease[]; amends: readonly BlAmend[];
  lois: readonly LoiTemplate[]; originals: readonly OriginalDoc[]; manifests: readonly Manifest[];
}

function finalTable(rows: readonly BlFinal[]): TabTable {
  return {
    cols: [['Job · HBL', 200], ['Khách', 220], ['Tàu · ATD', 240], ['Hạn (ATD + 2h làm việc)', 200], ['Còn lại', 150], ['Trạng thái', 128], ['', 126]],
    rows: rows.map((r) => {
      const l = finalLeft(r, NOW_MIN);
      const left = l.kind === 'sent' ? tx('—', undefined, { mood: 'lb' }) : l.kind === 'overdue' ? tx('Quá hạn', `từ ${l.since}`, { mood: 'err', strong: true }) : tx(`${l.hours}h${String(l.minutes).padStart(2, '0')}′`, 'giờ làm việc', { mood: l.soon ? 'warn' : undefined, strong: true, clock: true });
      const st = r.st === 'sent' ? chip('Đã gửi · FinalBL', 'green', r.sentAt) : r.st === 'fail' ? chip('Gửi lỗi', 'red', 'FinalBL chưa bật') : chip('Chưa gửi', 'amber');
      return { id: r.id, cells: [lk(r.job, r.hbl), tx(r.cus, undefined, { strong: true }), tx(r.vsl, `ATD ${r.atd}`), tx(r.due, r.note), left, st], act: r.st === 'sent' ? { k: 'Đã gửi', cls: 'done' } : { k: r.st === 'fail' ? 'Gửi lại' : 'Gửi Final', cls: 'pri' } };
    }),
    foot: `${rows.filter((r) => r.st !== 'sent').length} Final chưa gửi · task FBL`,
    empty: 'Không có Final nào cần gửi', emptySub: 'Task FBL sinh khi xác nhận ATD.',
  };
}

function releaseTable(rows: readonly BlRelease[]): TabTable {
  return {
    cols: [['Job', 172], ['Khách · yêu cầu qua', 210], ['Loại', 110], ['Công nợ · quá hạn', 170], ['Bước hiện tại', 250], ['Còn thiếu', 210], ['', 142]],
    rows: rows.map((r) => {
      const done = r.step >= 5;
      const sub = done ? 'RLS bật' : r.rej ? r.rej.why : r.step === 2 ? r.lvl : r.step === 3 ? 'Chờ đủ điều kiện kỹ thuật' : r.step === 4 ? 'Sẵn sàng gửi' : '';
      const act: Act = done ? { k: 'Đã release', cls: 'done' } : r.step === 4 ? { k: 'Gửi Release', cls: 'pri' } : r.step === 2 && r.mine ? { k: 'Duyệt', cls: 'pri' } : { k: r.rej ? 'Xem lý do' : 'Xem', cls: 'ghost' };
      return {
        id: r.id,
        cells: [
          lk(r.job, r.when), tx(r.cus, `qua ${r.via}`, { strong: true }), chip(r.type, TYPE_TONE[r.type]),
          tx(yn(r.money.debt), r.money.over ? 'có quá hạn' : 'không quá hạn', { strong: true }),
          tx(releaseStepLabel(r, RELEASE_STEPS), sub, { strong: true, mood: r.rej ? 'err' : r.self && r.step === 2 ? 'warn' : undefined }),
          tx(r.missing.length ? r.missing[0]! : done ? '—' : 'Đủ', r.missing.length > 1 ? `+${r.missing.length - 1}` : undefined, { mood: r.missing.length ? 'warn' : 'ok' }),
        ],
        act,
      };
    }),
    foot: 'Luồng: CS đề nghị → Sale → (còn nợ) Trưởng KD / KTT / BOD theo mức nợ → điều kiện kỹ thuật → gửi. OBL bản gốc cùng luật.',
    empty: 'Không có yêu cầu release', emptySub: 'Khách yêu cầu release trên myHANA hoặc mail sẽ hiện ở đây.',
  };
}

const AMD_ST = { fee: ['Đã tính phí', 'blue'], wait: ['Chờ Trưởng CS', 'amber'], waived: ['Đã bỏ phí', 'green'] } as const;
function amendTable(rows: readonly BlAmend[]): TabTable {
  return {
    cols: [['Job · HBL', 200], ['Khách', 210], ['Nội dung sửa', 330], ['Phí BL Amendment', 150], ['Lỗi do', 100], ['Trạng thái', 148], ['', 126]],
    rows: rows.map((r) => ({
      id: r.id,
      cells: [lk(r.job, r.hbl), tx(r.cus, undefined, { strong: true }), tx(r.what), tx(yn(r.money.fee), 'mã phí AMD'), tx(r.by, undefined, { strong: true, mood: r.by === 'HANA' ? 'err' : undefined }), chip(AMD_ST[r.st][0], AMD_ST[r.st][1])],
      act: r.st === 'wait' ? { k: 'Duyệt bỏ phí', cls: 'pri' } : { k: 'Xem', cls: 'ghost' },
    })),
    foot: 'Sửa sau phát hành sinh phí BL Amendment; lỗi HANA → Trưởng CS (giao tạm: THUỶ) duyệt bỏ phí, ghi lý do.',
    empty: 'Không có bill sửa sau phát hành', emptySub: '',
  };
}

function loiTable(rows: readonly LoiTemplate[]): TabTable {
  return {
    cols: [['Hãng', 170], ['Loại LOI', 220], ['Phiên bản · hiệu lực', 190], ['Chủ mẫu', 230], ['Đã dùng', 110], ['Tình trạng', 218], ['', 126]],
    rows: rows.map((r) => ({
      id: r.id,
      cells: [
        tx(r.car, undefined, { strong: true }), tx(r.kind), tx(r.ver, r.from === '—' ? undefined : `từ ${r.from}`), tx('Trưởng CS', 'giao tạm: THUỶ'), tx(`${r.uses} lần`),
        r.st === 'ok' ? chip('Đang dùng', 'green') : r.st === 'none' ? chip('Chưa có mẫu hãng', 'red', 'đang dùng mẫu HANA chuẩn') : chip('Cần rà lại', 'amber', 'hãng đổi mẫu 01/Oct'),
      ],
      act: r.st === 'none' ? { k: 'Tải mẫu lên', cls: 'pri' } : { k: 'Xem mẫu', cls: 'ghost' },
    })),
    foot: 'Trường tự điền: số HBL/MBL, tàu/chuyến, POL/POD, shipper, consignee, notify, container/seal, ngày, người ký.',
    empty: 'Chưa có mẫu LOI', emptySub: '',
  };
}

const ORIG_ST = { hana: ['HANA', 'blue'], cus: ['Khách', 'green'], ship: ['Chuyển phát', 'amber'], agent: ['Đại lý', 'violet'], late: ['Chuyển phát · trễ', 'red'] } as const;
function originalTable(rows: readonly OriginalDoc[]): TabTable {
  return {
    cols: [['Loại · Số', 210], ['Job · Khách', 240], ['Đang ở', 190], ['Mã vận đơn', 190], ['Ký nhận / ghi chú', 304], ['', 130]],
    rows: rows.map((r) => ({
      id: r.id,
      cells: [tx(r.kind, r.no, { strong: true }), lk(r.job, r.cus), chip(ORIG_ST[r.st][0], ORIG_ST[r.st][1], r.at), tx(r.trk), tx(r.sign, undefined, { mood: r.st === 'late' ? 'err' : 'tx2' })],
      act: r.st === 'hana' ? { k: 'Giao cho khách', cls: 'pri' } : { k: 'Lịch sử', cls: 'ghost' },
    })),
    foot: 'Giao tay → ký nhận trên HANA Ezy mobile / HANA Mov (ảnh + chữ ký + giờ + GPS).',
    empty: 'Chưa có chứng từ gốc', emptySub: '',
  };
}

function manifestTable(rows: readonly Manifest[]): TabTable {
  return {
    cols: [['Job · HBL', 210], ['Cảng đích', 120], ['Loại khai', 230], ['Hạn khai', 180], ['Ghi nhận', 220], ['Phát hành HBL', 160], ['', 124]],
    rows: rows.map((r) => ({
      id: r.id,
      cells: [
        lk(r.job, r.hbl), tx(r.dest), tx(r.kind, 'khai ngoài hệ thống'), tx(r.due),
        r.st === 'done' ? chip('Đã khai', 'green', r.by) : r.st === 'na' ? chip('Không cần', 'grey') : chip('Chưa ghi nhận', 'amber'),
        r.st === 'todo' ? tx('Đang chặn', 'bỏ chặn khi ghi nhận', { mood: 'err', strong: true }) : tx('Mở', undefined, { mood: 'ok', strong: true }),
      ],
      act: r.st === 'todo' ? { k: 'Ghi nhận đã khai', cls: 'pri' } : { k: 'Xem', cls: 'ghost' },
    })),
    foot: 'Khai e-Manifest / AMS / ENS / ACI ở hệ thống ngoài; HANA Ezy chỉ nhắc và chặn phát hành HBL đến khi ghi nhận.',
    empty: 'Không có lô cần khai manifest', emptySub: '',
  };
}

/** Dựng bảng của một tab ngoài Draft, đã lọc theo ô tìm. */
export function buildTab(tab: Exclude<BlTab, 'draft'>, d: TabData, q: string): TabTable {
  switch (tab) {
    case 'final': return finalTable(filterBl(d.finals, { q }));
    case 'rel': return releaseTable(filterBl(d.releases, { q }));
    case 'amd': return amendTable(filterBl(d.amends, { q }));
    case 'loi': return loiTable(filterBl(d.lois, { q }));
    case 'orig': return originalTable(filterBl(d.originals, { q }));
    case 'mnf': return manifestTable(filterBl(d.manifests, { q }));
  }
}

const MOOD: Record<Mood, string> = { err: s.err!, ok: s.ok!, warn: s.warnText!, lb: s.lb!, tx2: s.tx2! };

function CellView({ c }: { c: Cell }): ReactNode {
  if (c.kind === 'chip') {
    return (
      <span className={`${s.pill} ${s[`p_${c.tone}`]}`} title={c.tip}>
        <span className={s.dot} aria-hidden="true" />
        {c.v}
      </span>
    );
  }
  const cls = c.kind === 'link' ? s.link : `${c.strong ? s.main : ''} ${c.mood ? MOOD[c.mood] : ''}`;
  return (
    <>
      <div className={cls}>{c.v}</div>
      {c.sub && (
        <div className={s.sub} title={c.sub}>
          {c.clock && <IconClock size={12} stroke={2} aria-hidden="true" />}
          <span>{c.sub}</span>
        </div>
      )}
    </>
  );
}

export function TabRows({ t, onOpen, onAct }: { t: TabTable; onOpen: (id: string) => void; onAct: () => void }): ReactNode {
  return (
    <>
      {t.rows.map((r) => (
        <tr key={r.id} onClick={() => onOpen(r.id)}>
          {r.cells.map((c, i) => (
            <td key={i}>
              <CellView c={c} />
            </td>
          ))}
          <td className={s.end}>
            <button
              type="button"
              className={`${s.act} ${r.act.cls === 'pri' ? s.a_pri : r.act.cls === 'done' ? s.a_done : s.a_ghost}`}
              disabled={r.act.cls === 'done'}
              onClick={(e) => {
                e.stopPropagation();
                onAct();
              }}
            >
              {r.act.cls === 'done' && <IconCircleCheck size={16} aria-hidden="true" />}
              {r.act.k}
            </button>
          </td>
        </tr>
      ))}
    </>
  );
}
